import { useTranslation } from 'react-i18next'
import { Loader2, Upload } from 'lucide-react'
import { FileExplorerItem, DragDropHandlers } from '@features/file-explorer/types'
import GridCard from './GridCard'

interface GridViewProps {
  items: FileExplorerItem[]
  getItemKey: (item: FileExplorerItem) => string
  onItemClick: (item: FileExplorerItem) => void
  onSelectItem: (item: FileExplorerItem, index: number, shiftKey: boolean) => void
  onDelete: (item: FileExplorerItem) => void
  onRename: (item: FileExplorerItem) => void
  onDownload: (item: FileExplorerItem) => void
  onShow: (item: FileExplorerItem) => void
  onManagePermissions: (item: FileExplorerItem) => void
  isLoading: boolean
  dragAndDrop: DragDropHandlers
}

export default function GridView({
  items,
  getItemKey,
  onItemClick,
  onSelectItem,
  onDelete,
  onRename,
  onDownload,
  onShow,
  onManagePermissions,
  isLoading,
  dragAndDrop
}: GridViewProps) {
  const { t } = useTranslation()

  return (
    <div
      className="bg-base-100 p-4 relative select-none"
      onDragEnter={dragAndDrop.handleDragEnter}
      onDragOver={dragAndDrop.handleDragOver}
      onDragLeave={dragAndDrop.handleDragLeave}
      onDrop={(e) => dragAndDrop.handleDrop(e)}
      data-testid="grid-view-drop-zone"
    >
      {/* Drop Zone Overlay - only show for file uploads, not for item moves */}
      {dragAndDrop.isDragging && !dragAndDrop.draggedItem && (
        <div className="absolute inset-0 bg-primary/10 border-4 border-dashed border-primary z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-base-100 p-6 rounded-lg shadow-lg">
            <Upload className="w-12 h-12 text-primary mx-auto mb-2" />
            <p className="text-lg font-semibold text-primary">
              {t('fileExplorer.dragDrop.dropZone.title')}
            </p>
            <p className="text-sm text-base-content/70 mt-1">
              {t('fileExplorer.dragDrop.dropZone.subtitle')}
            </p>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 text-base-content/50">
          {t('fileExplorer.table.noItems')}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {items.map((item, index) => (
            <GridCard
              key={`${item.type}-${item.id}`}
              item={item}
              items={items}
              index={index}
              isLoading={isLoading}
              dragAndDrop={dragAndDrop}
              onItemClick={onItemClick}
              onSelectItem={onSelectItem}
              onDelete={onDelete}
              onRename={onRename}
              onDownload={onDownload}
              onShow={onShow}
              onManagePermissions={onManagePermissions}
              getItemKey={getItemKey}
            />
          ))}
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-base-100/20 flex items-center justify-center z-10 pointer-events-auto">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}
    </div>
  )
}
